import React, { useEffect, useState } from 'react';
import { FaBook, FaVideo, FaYoutube, FaGoogleDrive, FaFileAlt, FaSpinner, FaEye, FaPlay, FaDownload, FaTimes, FaExpand, FaExternalLinkAlt, FaLock } from 'react-icons/fa';
import axios from 'axios';
import UpgradeModal from './UpgradeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Content Viewer Modal Component
const ContentViewerModal = ({ content, onClose }) => {
  if (!content) return null;

  // Convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // Handle various YouTube URL formats
    let videoId = null;
    
    // youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) videoId = watchMatch[1];
    
    // youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) videoId = shortMatch[1];
    
    // youtube.com/embed/VIDEO_ID
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) videoId = embedMatch[1];
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return url;
  };

  // Convert Google Drive URL to embed URL
  const getGoogleDriveEmbedUrl = (url) => {
    if (!url) return null;
    
    // Extract file ID from various Google Drive URL formats
    let fileId = null;
    
    // drive.google.com/file/d/FILE_ID/view
    const fileMatch = url.match(/\/file\/d\/([^/]+)/);
    if (fileMatch) fileId = fileMatch[1];
    
    // drive.google.com/open?id=FILE_ID
    const openMatch = url.match(/[?&]id=([^&]+)/);
    if (openMatch) fileId = openMatch[1];
    
    // docs.google.com/document/d/FILE_ID
    const docMatch = url.match(/\/document\/d\/([^/]+)/);
    if (docMatch) fileId = docMatch[1];
    
    // docs.google.com/presentation/d/FILE_ID
    const presentationMatch = url.match(/\/presentation\/d\/([^/]+)/);
    if (presentationMatch) fileId = presentationMatch[1];
    
    // docs.google.com/spreadsheets/d/FILE_ID
    const sheetMatch = url.match(/\/spreadsheets\/d\/([^/]+)/);
    if (sheetMatch) fileId = sheetMatch[1];
    
    if (fileId) {
      // Check if it's a presentation
      if (url.includes('/presentation/')) {
        return `https://docs.google.com/presentation/d/${fileId}/embed?start=false&loop=false&delayms=3000`;
      }
      // Check if it's a document
      if (url.includes('/document/')) {
        return `https://docs.google.com/document/d/${fileId}/preview`;
      }
      // Check if it's a spreadsheet
      if (url.includes('/spreadsheets/')) {
        return `https://docs.google.com/spreadsheets/d/${fileId}/preview`;
      }
      // Default to file preview
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }
    return url;
  };

  // Get embed URL based on content type
  const getEmbedUrl = () => {
    const url = content.external_url || content.url;
    
    if (!url) return null;
    
    switch (content.content_type) {
      case 'youtube':
        return getYouTubeEmbedUrl(url);
      case 'gdrive':
      case 'video':
        if (url.includes('google.com') || url.includes('drive.google')) {
          return getGoogleDriveEmbedUrl(url);
        }
        return url;
      case 'pdf':
        // For PDFs, use Google Docs Viewer or direct embed
        if (url.includes('drive.google')) {
          return getGoogleDriveEmbedUrl(url);
        }
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      case 'ppt':
        // For PPTs from Google Drive
        if (url.includes('google.com')) {
          return getGoogleDriveEmbedUrl(url);
        }
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      default:
        return url;
    }
  };

  const embedUrl = getEmbedUrl();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-[#0F172A] rounded-2xl w-full max-w-6xl h-[85vh] m-4 flex flex-col border border-[#2D5A8F] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2D5A8F]">
          <div className="flex items-center space-x-3">
            {content.content_type === 'youtube' && <FaYoutube className="text-red-500 text-xl" />}
            {content.content_type === 'gdrive' && <FaGoogleDrive className="text-green-500 text-xl" />}
            {content.content_type === 'pdf' && <FaFileAlt className="text-blue-500 text-xl" />}
            {content.content_type === 'ppt' && <FaFileAlt className="text-orange-500 text-xl" />}
            {content.content_type === 'video' && <FaVideo className="text-purple-500 text-xl" />}
            <div>
              <h3 className="text-white font-bold text-lg">{content.title}</h3>
              {content.description && (
                <p className="text-gray-400 text-sm line-clamp-1">{content.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <a
              href={content.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-400 hover:text-[#FFD700] hover:bg-[#1E3A5F] rounded-lg transition-all"
              title="Open in new tab"
            >
              <FaExternalLinkAlt />
            </a>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
              title="Close"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Content Viewer */}
        <div className="flex-1 p-2 bg-[#1A2942]">
          <iframe
            src={embedUrl}
            title={content.title}
            className="w-full h-full rounded-lg border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
          />
        </div>
      </div>
    </div>
  );
};

const ContentLibrary = ({ title = "Learning Resources", isFreePlan = false }) => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'youtube': return <FaYoutube className="text-red-500" size={20} />;
      case 'gdrive': return <FaGoogleDrive className="text-green-500" size={20} />;
      case 'pdf': return <FaFileAlt className="text-blue-500" size={20} />;
      case 'ppt': return <FaFileAlt className="text-orange-500" size={20} />;
      case 'ebook': return <FaBook className="text-yellow-500" size={20} />;
      case 'video': return <FaVideo className="text-purple-500" size={20} />;
      default: return <FaFileAlt className="text-gray-500" size={20} />;
    }
  };

  const getContentTypeLabel = (type) => {
    switch (type) {
      case 'youtube': return 'YouTube Video';
      case 'gdrive': return 'Google Drive';
      case 'pdf': return 'PDF Document';
      case 'ppt': return 'PowerPoint';
      case 'ebook': return 'eBook';
      case 'video': return 'Video';
      default: return type;
    }
  };

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'training', label: 'Training' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'resources', label: 'Resources' }
  ];

  const filteredContent = activeCategory === 'all' 
    ? content 
    : content.filter(item => item.category === activeCategory);

  const handleContentClick = (item) => {
    setSelectedContent(item);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-center h-32">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  if (content.length === 0) {
    return null; // Don't show section if no content
  }

  return (
    <>
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]" data-testid="content-library">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center space-x-3">
            <FaBook className="text-[#FFD700]" />
            <span>{title}</span>
          </h2>
          <span className="text-gray-400 text-sm">{content.length} items</span>
        </div>

        {/* Category Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929]'
                  : 'bg-[#0F172A] text-gray-300 hover:bg-[#1E3A5F]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContent.map((item) => {
            const isYoutube = item.content_type === 'youtube';
            const isLocked = isFreePlan && !isYoutube;
            
            return (
            <div 
              key={item.id} 
              className={`bg-[#0F172A] rounded-xl p-4 border transition-all group relative ${
                isLocked ? 'border-[#2D5A8F]/50 opacity-75' : 'border-[#2D5A8F] hover:border-[#FFD700]'
              }`}
            >
              {/* Content Type Badge */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {getContentIcon(item.content_type)}
                  <span className="text-xs text-gray-400 uppercase">{getContentTypeLabel(item.content_type)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {item.category && (
                    <span className="px-2 py-1 bg-[#1E3A5F] text-[#FFD700] text-xs rounded-full">
                      {item.category}
                    </span>
                  )}
                  {isLocked && <FaLock className="text-gray-500" size={12} />}
                </div>
              </div>

              {/* Title & Description */}
              <h3 className={`font-bold mb-2 line-clamp-2 ${isLocked ? 'text-gray-500' : 'text-white group-hover:text-[#FFD700] transition-colors'}`}>
                {item.title}
              </h3>
              {item.description && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
              )}

              {/* Action Button */}
              {(item.external_url || item.url) && (
                isLocked ? (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="flex items-center justify-center space-x-2 w-full py-2 bg-gray-700/30 text-gray-500 rounded-lg transition-all font-medium hover:bg-gray-700/50 hover:text-gray-400"
                    data-testid={`content-locked-btn-${item.id}`}
                  >
                    <FaLock size={12} />
                    <span>Upgrade to Access</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => handleContentClick({...item, external_url: item.external_url || item.url})}
                    className="flex items-center justify-center space-x-2 w-full py-2 bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] hover:from-[#D4AF37] hover:to-[#FFD700] text-white hover:text-[#0A1929] rounded-lg transition-all font-medium"
                    data-testid={`content-view-btn-${item.id}`}
                  >
                    {item.content_type === 'youtube' || item.content_type === 'video' ? (
                      <>
                        <FaPlay size={12} />
                        <span>Watch Now</span>
                      </>
                    ) : item.content_type === 'pdf' || item.content_type === 'ebook' || item.content_type === 'ppt' ? (
                      <>
                        <FaEye size={12} />
                        <span>View Document</span>
                      </>
                    ) : item.content_type === 'gdrive' ? (
                      <>
                        <FaGoogleDrive size={12} />
                        <span>Open in Drive</span>
                      </>
                    ) : (
                      <>
                        <FaEye size={12} />
                        <span>View</span>
                      </>
                    )}
                  </button>
                )
              )}
            </div>
          );
          })}
        </div>

        {filteredContent.length === 0 && (
          <p className="text-gray-400 text-center py-8">No content in this category</p>
        )}
      </div>

      {/* Content Viewer Modal */}
      {selectedContent && (
        <ContentViewerModal 
          content={selectedContent} 
          onClose={() => setSelectedContent(null)} 
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </>
  );
};

export default ContentLibrary;
